import React, { useState, useEffect, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

// Components
import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
} from "../components/formStyles";
import CurrencyInput from 'react-currency-input-field';
import {
  deleteData,
  getAlert,
  getDataWithComp,
  getDataWithJoin,
  insertData,
  parseCurrency,
  updateData,
  updateQty,
} from "../../../../utils/SamuiUtils";
import { useNavigate } from "react-router-dom";
import { manageWhItemData } from "../../../../utils/WarehouseUtils";
import ViewHistoryItem from "../../../components/Modal/ViewHistoryItem";

const DEFAULT_IMAGE_MIME = "image/png";

const detectImageMime = (value) => {
  if (!value || typeof value !== "string") {
    return DEFAULT_IMAGE_MIME;
  }

  if (value.startsWith("data:image/")) {
    const match = value.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,/);
    return match ? match[1] : DEFAULT_IMAGE_MIME;
  }

  const signature = value.slice(0, 20);
  if (signature.startsWith("/9j/") || signature.startsWith("/9J/")) {
    return "image/jpeg";
  }
  if (signature.startsWith("iVBOR")) {
    return "image/png";
  }
  if (signature.startsWith("R0lGOD")) {
    return "image/gif";
  }
  if (signature.startsWith("Qk")) {
    return "image/bmp";
  }
  return DEFAULT_IMAGE_MIME;
};

const buildImagePreviewSrc = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }
  if (value.startsWith("data:image/")) {
    return value;
  }
  const mime = detectImageMime(value);
  return `data:${mime};base64,${value}`;
};

const Form = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
  embedded = false,
  onBack,
}) => {
  const navigate = useNavigate();

  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Prevent shortcuts when typing in input fields
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.tagName === 'SELECT') {
        return;
      }

      // ESC key - Cancel/Back
      if (event.key === 'Escape') {
        event.preventDefault();
        if (window.confirm('ต้องการยกเลิกการทำงานหรือไม่?')) {
          if (typeof onBack === 'function') {
            onBack();
          } else {
            navigate("/tbsetitem");
          }
        }
      }

      // F10 key - Save
      if (event.key === 'F10') {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (embedded) return;

    const isVisible = (el) => {
      if (!el) return false;
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
    };

    const selector = 'input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const getFocusable = (container) => {
      if (!container) return [];
      return Array.from(container.querySelectorAll(selector)).filter(isVisible);
    };

    const handleArrow = (event) => {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (!keys.includes(event.key)) return;

      let container = null;
      const itemInput = document.querySelector('input[name="item_code"]');
      if (itemInput) container = itemInput.closest('.card-body');
      if (!container) container = document.querySelector('.container-fluid .card-body') || document.querySelector('.card-body');
      if (!container) return;

      const focusables = getFocusable(container);
      if (!focusables || focusables.length === 0) return;

      const active = document.activeElement;
      const index = focusables.indexOf(active);

      event.preventDefault();
      event.stopPropagation();

      let newIndex = 0;
      if (index === -1) {
        newIndex = (event.key === 'ArrowDown' || event.key === 'ArrowRight') ? 0 : focusables.length - 1;
      } else {
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          newIndex = index < focusables.length - 1 ? index + 1 : 0;
        } else {
          newIndex = index > 0 ? index - 1 : focusables.length - 1;
        }
      }

      const toFocus = focusables[newIndex];
      if (toFocus) {
        toFocus.focus();
      }
    };

    window.addEventListener('keydown', handleArrow, true);
    return () => window.removeEventListener('keydown', handleArrow, true);
  }, [embedded]);
  const [unitDataList, setUnitDataList] = useState([]);
  const [groupDataList, setGroupDataList] = useState([])
  const [beginAmount] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  useEffect(() => {
    const compId = localStorage.getItem("company");
    if (compId) {
      setFormData((prevData) => ({
        ...prevData,
        comp_id: compId,
      }));
    }
  }, [setFormData]);

  useEffect(() => {
    if (mode === "I") {
      const initialData = {
        item_id: "",
        comp_id: parseCurrency(window.localStorage.getItem("company")),
        item_barcode: "",
        item_code: "",
        item_name: "",
        item_name_en: "",
        item_short_name: "",
        item_unit_in: "",
        item_unit_st: "",
        item_unit_out: "",
        item_cost: "",
        item_price: "",
        item_pricea: "",
        item_priceb: "",
        item_brand: "",
        item_series: "",
        item_group: "",
        item_cat: "",
        item_colour: "",
        item_size: "",
        item_status: "",
        item_desc: "",
        item_popoint: "",
        item_maxonhand: "",
        ap_code: "",
        item_gold: "",
        item_onhand: "",
        item_wh: "",
        item_wholesale_price: "",
        item_img: "",
      };
      initialize();
      setFormData(initialData);
      setInitialSnapshot(initialData);
      setImagePreview(buildImagePreviewSrc(initialData.item_img));
    } else if (mode === "U" && selectedData) {
      const initialData = {
        item_id: parseCurrency(selectedData.Item_Id, 10),
        comp_id: parseCurrency(selectedData.Comp_Id, 10),
        item_barcode: selectedData.Item_Barcode,
        item_code: selectedData.Item_Code,
        item_name: selectedData.Item_Name,
        item_name_en: selectedData.Item_Name_EN,
        item_short_name: selectedData.Item_Short_Name,
        item_unit_in: selectedData.Item_Unit_IN,
        item_unit_st: selectedData.Item_Unit_ST,
        item_unit_out: selectedData.Item_Unit_OUT,
        item_cost: selectedData.Item_Cost,
        item_price: selectedData.Item_Price,
        item_pricea: selectedData.Item_PriceA,
        item_priceb: selectedData.Item_PriceB,
        item_brand: selectedData.Item_Brand,
        item_series: selectedData.Item_Series,
        item_group: selectedData.Item_Group,
        item_cat: selectedData.Item_Cat,
        item_colour: selectedData.Item_Colour,
        item_size: selectedData.Item_Size,
        item_status: selectedData.Item_Status,
        item_desc: selectedData.Item_Desc,
        item_popoint: parseCurrency(selectedData.Item_PoPoint, 10),
        item_maxonhand: parseCurrency(selectedData.Item_MaxOnhand, 10),
        ap_code: selectedData.AP_Code,
        item_gold: parseCurrency(selectedData.Item_Gold),
        item_onhand: parseCurrency(selectedData.Item_Onhand, 10),
        item_wh: selectedData.Item_WH,
        item_wholesale_price: selectedData.Item_Wholesale_price,
        item_img: selectedData.Item_Img || "",
      };
      initialize();
      setFormData(initialData);
      setInitialSnapshot(initialData);
      setImagePreview(buildImagePreviewSrc(initialData.item_img));
    }
  }, [mode, selectedData, setFormData]);

  useEffect(() => {
    setImagePreview(buildImagePreviewSrc(formData.item_img));
  }, [formData.item_img]);


  const initialize = async () => {
    try {
      const unitDataList = await getDataWithComp('Tb_Set_Unit', `AND Unit_Status = 'Y' ORDER BY Unit_Id ASC`);
      if (unitDataList && unitDataList.length > 0) {
        // setUnitDataList([])
        setUnitDataList(unitDataList);
      }
      // ค้นหาข้อมูลตั้งต้นจาก Tb_Set_ItmGroup
      const groupDataList = await getDataWithComp('Tb_Set_ItmGroup', `AND Group_Status = 'Y' ORDER BY Group_Id ASC`);
      if (groupDataList && groupDataList.length > 0) {
        // setWhDataList([])
        setGroupDataList(groupDataList);
      }
      // Removed: fetching for warehouse/brand/colour/category/size
    } catch (error) {
      getAlert('FAILED', error.message);
    }
  };

  const validateItemGroup = () => {
    if (
      formData.item_group === undefined ||
      formData.item_group === null ||
      formData.item_group === ""
    ) {
      getAlert('FAILED', 'กรุณาเลือกหมวดหมู่สินค้า');
      return false;
    }
    return true;
  };

  const validateRequiredFields = () => {
    if (!formData.item_name || String(formData.item_name).trim() === "") {
      getAlert('FAILED', 'กรุณากรอกชื่อสินค้า');
      return false;
    }
    if (!formData.item_unit_st || String(formData.item_unit_st).trim() === "") {
      getAlert('FAILED', 'กรุณาเลือกหน่วยในคลัง');
      return false;
    }
    return true;
  };

  const generateItemCode = async () => {
    try {
      const rows = await getDataWithComp("Tb_Set_Item", "ORDER BY Item_Id DESC");
      const maxId = rows && rows.length > 0 ? Number(rows[0].Item_Id || 0) : 0;
      return `ITM${String(maxId + 1).padStart(5, "0")}`;
    } catch {
      return `ITM${Date.now()}`;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleCurrencyChange = (value, name) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageChange = async (event) => {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      return;
    }

    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      getAlert("FAILED", "รองรับเฉพาะไฟล์ PNG หรือ JPEG เท่านั้น");
      event.target.value = "";
      return;
    }

    const maxFileSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxFileSize) {
      getAlert("FAILED", "ไฟล์มีขนาดใหญ่เกินไป (สูงสุด 5MB)");
      event.target.value = "";
      return;
    }

    try {
      const base64 = await convertFileToBase64(file);
      setFormData((prev) => ({
        ...prev,
        item_img: base64,
      }));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error converting image:", error);
      getAlert("FAILED", "ไม่สามารถอ่านไฟล์รูปภาพได้ กรุณาลองใหม่");
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      item_img: "",
    }));
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const sanitizeItemPayload = (data) => {
    const sanitized = { ...data };
    sanitized.item_cost = data.item_cost === "" ? 0 : data.item_cost;
    sanitized.item_price = data.item_price === "" ? 0 : data.item_price;
    sanitized.item_pricea = data.item_pricea === "" ? 0 : data.item_pricea;
    sanitized.item_priceb = data.item_priceb === "" ? 0 : data.item_priceb;
    sanitized.item_popoint = data.item_popoint === "" ? 0 : data.item_popoint;
    sanitized.item_maxonhand =
      data.item_maxonhand === "" ? 0 : data.item_maxonhand;
    sanitized.item_gold = data.item_gold === "" ? 0 : data.item_gold;
    sanitized.item_onhand = data.item_onhand === "" ? 0 : data.item_onhand;
    sanitized.item_wholesale_price =
      data.item_wholesale_price === "" ? 0 : data.item_wholesale_price;
    sanitized.item_img =
      data.item_img && data.item_img !== "" ? data.item_img : null;
    sanitized.item_status = data.item_status === "" ? "Y" : data.item_status;
    sanitized.item_sprice_use =
      data.item_sprice_use === "" || data.item_sprice_use === undefined || data.item_sprice_use === null
        ? "N"
        : data.item_sprice_use;
    sanitized.item_desc =
      data.item_desc && data.item_desc.trim() !== ""
        ? data.item_desc.trim()
        : null;
    sanitized.item_short_name = null;
    return sanitized;
  };

  // แปลง key จาก form (camel/underscore lowercase) เป็นชื่อคอลัมน์ในฐานข้อมูล
  const mapToDbColumns = (data) => {
    if (!data || typeof data !== 'object') return {};
    const map = {
      comp_id: 'Comp_Id',
      item_barcode: 'Item_Barcode',
      item_code: 'Item_Code',
      item_name: 'Item_Name',
      item_name_en: 'Item_Name_EN',
      item_short_name: 'Item_Short_Name',
      item_unit_in: 'Item_Unit_IN',
      item_unit_st: 'Item_Unit_ST',
      item_unit_out: 'Item_Unit_OUT',
      item_cost: 'Item_Cost',
      item_price: 'Item_Price',
      item_pricea: 'Item_PriceA',
      item_priceb: 'Item_PriceB',
      item_brand: 'Item_Brand',
      item_series: 'Item_Series',
      item_group: 'Item_Group',
      item_cat: 'Item_Cat',
      item_colour: 'Item_Colour',
      item_color: 'Item_Colour',
      item_size: 'Item_Size',
      item_status: 'Item_Status',
      item_popoint: 'Item_PoPoint',
      item_maxonhand: 'Item_MaxOnhand',
      ap_code: 'AP_Code',
      item_gold: 'Item_Gold',
      item_onhand: 'Item_Onhand',
      item_wh: 'Item_WH',
      item_sprice_use: 'Item_SPrice_use',
      item_wholesale_price: 'Item_Wholesale_price',
      item_img: 'Item_Img',
      item_desc: 'Item_Desc',
      item_is_service: 'Item_Is_Service',
      item_qty: 'Item_Qty',
      item_is_sale: 'Item_Is_Sale',
    };

    const result = {};
    Object.keys(data).forEach((k) => {
      const dbKey = map[k];
      if (dbKey) {
        result[dbKey] = data[k];
      }
    });
    return result;
  };

  const handleInsertTbsetItem = async () => {
    if (!validateRequiredFields()) return false;

    // auto-gen รหัสสินค้าถ้าไม่ได้กรอก
    let resolvedCode = String(formData.item_code || "").trim();
    if (!resolvedCode) {
      resolvedCode = await generateItemCode();
    }

    const okCode = await checkItemCode(resolvedCode);
    if (!okCode) return false;

    if (!validateItemGroup()) return false;

    try {
      const { item_id, ...dataToInsert } = { ...formData, item_code: resolvedCode };
      const sanitizedData = sanitizeItemPayload(dataToInsert);

      // แปลงเป็นชื่อคอลัมน์ฐานข้อมูล
      const dbPayload = mapToDbColumns(sanitizedData);
      const fields = Object.keys(dbPayload).join(", ");
      const values = Object.values(dbPayload);
      const insertRes = await insertData("Tb_Set_Item", fields, values);

      if (insertRes?.status === 'FAILED') {
        console.error('Insert failed:', insertRes.message);
        getAlert('FAILED', insertRes.message || 'ไม่สามารถบันทึกข้อมูลได้');
        return false;
      }

      // ทำการ auto ไปอยู่คลังนั้นเลย กรณี สร้างสินค้าใหม่
      const getItemData = await getDataWithComp(
        "Tb_Set_Item",
        `
            AND Item_Code = '${sanitizedData.item_code}' 
            AND Item_Name = '${sanitizedData.item_name}' 
            AND Item_WH = '${sanitizedData.item_wh}' 
            AND Item_Status = 'Y'
          `
      );
      if (getItemData && getItemData.length > 0) {
        await manageWhItemData(
          getItemData[0].Item_Id, // Item ไอดีของสิ่งนั้นๆ
          dataToInsert.item_code, // Item โค้ดของสิ่งนั้นๆ
          dataToInsert.item_name, // ชื่อ Item นั้นๆ
          parseCurrency(dataToInsert.item_wh), // WarehouseID ของไอเทม
          "BG", // ประเภท เช่น IN/OUT/AI/AO/TI/TO
          parseCurrency(beginAmount || 0), // ค่าจำนวนที่กรอกจากหน้าจอ
          null, // ไอดีเอกสาร
          "เริ่มต้นคลัง", // เลขเอกสาร
          "เริ่มต้นคลัง", // เลขเอกสารอ้างอิง
          null // หมายเหตุ
        );
      }
      return true;
    } catch (error) {
      console.error("Error inserting data:", error);
      getAlert(
        "FAILED",
        "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
      return false;
    }
  };

  const formatValues = (data) => {
    const { item_id, ...rest } = data;
    const sanitized = sanitizeItemPayload(rest);
    return mapToDbColumns(sanitized);
  };

  const handleUpdateTbsetItem = async () => {
    if (!validateRequiredFields()) {
      return false;
    }

    const okCode = await checkItemCode(formData.item_code, formData.item_id);
    if (!okCode) return;

    if (!validateItemGroup()) {
      return false;
    }
    try {
      const where = { Item_Id: formData.item_id };
      const allValues = formatValues(formData);
      // แยก Item_Img ออกเพราะ DB เป็น varbinary ต้องอัปเดตแยก
      const { Item_Img, ...values } = allValues;
      const res = await updateData("Tb_Set_Item", values, where);
      if (res) {

        // ตอนที่อัพเดทชื่อสินค้า ให้ไป อัพเดทชื่อสินค้าที่ onHand ด้วย
        await updateQty(
          "WH_ITEM_Onhand",
          `Item_Name = '${formData.item_name}'`,
          `WHERE Item_Id = ${parseCurrency(formData.item_id)}`
        );
        return true;
      }
      return false;
    } catch (error) {
      getAlert("FAILED", "ไม่สามารถอัปเดตข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว");
      return false;
    }
  };

  const handleDelete = async () => {
    try {
      getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
        if (isConfirm) {
          await deleteData(
            "Tb_Set_Item",
            `WHERE Item_Id = ${selectedData.Item_Id}`
          );
          await callInitialize();
        }
      })
    } catch (error) {
      getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  const handleReset = () => {
    if (!initialSnapshot) {
      return;
    }
    setFormData(initialSnapshot);
    setImagePreview(buildImagePreviewSrc(initialSnapshot.item_img));
  };

  const handleSubmit = async (e) => {
    if (isSavingRef.current) return;
    try {
      if (!validateRequiredFields()) {
        return;
      }

      if (mode === "I" && !validateItemGroup()) {
        return;
      }

      isSavingRef.current = true;
      setIsSaving(true);

      let opResult = false;
      if (mode === "I") {
        opResult = await handleInsertTbsetItem();
      } else if (mode === "U") {
        opResult = await handleUpdateTbsetItem();
      }

      if (opResult) {
        await callInitialize();
        setFormData({
        item_id: "", // Int
        comp_id: parseCurrency(window.localStorage.getItem("company"), 10),
        item_barcode: "",
        item_code: "",
        item_name: "",
        item_name_en: "",
        item_short_name: "",
        item_unit_in: "",
        item_unit_st: "",
        item_unit_out: "",
        item_cost: "",
        item_price: "",
        item_pricea: "",
        item_priceb: "",
        item_brand: "",
        item_series: "",
        item_group: "",
        item_cat: "",
        item_color: "",
        item_size: "",
        item_status: "",
        item_desc: "",
        item_popoint: "",
        item_maxonhand: "",
        ap_code: "",
        item_gold: "",
        item_onhand: "",
        item_wh: "",
        item_wholesale_price: "",
        item_img: "",
      });
      setImagePreview(null);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      getAlert(
        "WARNING",
        "โปรดตรวจสอบข้อมูลอีกครั้ง ข้อมูลนี้มีอยู่ในระบบแล้ว"
      );
      return null;
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const checkItemCode = async (code = formData.item_code, excludeId = null) => {
    const value = String(code || "").trim();
    if (!value) return true;

    const safeCode = value.replace(/'/g, "''");
    const excludeClause = excludeId ? ` AND a.Item_Id != ${parseCurrency(excludeId, 10)}` : "";

    try {
      const resItemCode = await getDataWithJoin(
        `a.*`,
        `[dbo].[Tb_Set_Item] AS a`,
        `WHERE a.Item_Code = '${safeCode}' ${excludeClause}`,
        ""
      );

      if (resItemCode && resItemCode.length > 0) {
        getAlert('FAILED', 'มีรหัสสินค้าแล้ว');
        return false;
      }
      return true;
    } catch (err) {
      console.error('checkItemCode error:', err);
      getAlert('FAILED', 'ไม่สามารถตรวจสอบรหัสสินค้าได้ กรุณาลองใหม่');
      return false;
    }
  };

  return (
    <>
      {embedded ? (
        <InitialDataFormLayout
          name={name}
          entityPath="/tbsetitem"
          mode={mode}
          onSubmit={handleSubmit}
          isSubmittingDisabled={isSaving}
          onBack={onBack}
          onReset={handleReset}
          callInitialize={callInitialize}
          hideHeader={true}
          submitLabelInsert={"บันทึก"}
        >
          <div className="row g-3">
            <div className="col-md-6">
              <label style={labelStyle}>รหัสสินค้า {mode === "U" && <span style={{ color: '#dc2626' }}>*</span>}</label>
              <input
                type="text"
                className="form-control"
                name="item_code"
                value={formData.item_code || ""}
                onChange={handleChange}
                style={inputStyle}
                placeholder={mode === "I" ? "ระบบจะสร้างรหัสให้อัตโนมัติหากไม่กรอก" : ""}
              />
            </div>
            <div className="col-md-6">
              <label style={labelStyle}>ชื่อสินค้า <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="text"
                className="form-control"
                name="item_name"
                value={formData.item_name || ""}
                onChange={handleChange}
                style={inputStyle}
                required
              />
            </div>

            <div className="col-md-6">
              <label style={labelStyle}>หมวดหมู่สินค้า <span style={{ color: '#dc2626' }}>*</span></label>
              <select
                name="item_group"
                className="form-select"
                value={formData.item_group || ""}
                onChange={handleChange}
                disabled={groupDataList.length <= 0}
                style={selectStyle}
                required
              >
                <option value={""}>กรุณาเลือกหมวดหมู่สินค้า</option>
                {groupDataList.map((group) => (
                  <option key={group.Group_Id} value={group.Group_Id}>
                    {group.Group_Name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12" />
          </div>
        </InitialDataFormLayout>
      ) : (
        <InitialDataFormLayout
          name={name}
          entityPath="/tbsetitem"
          mode={mode}
          onSubmit={handleSubmit}
          isSubmittingDisabled={isSaving}
          onBack={onBack || (() => navigate("/tbsetitem"))}
          hideHeader={embedded}
          onReset={handleReset}
          onDelete={mode === "U" ? handleDelete : undefined}
          callInitialize={callInitialize}
          hidePageTitle
          contentCard={false}
          footerPreset="product"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: "none" }}
            onChange={handleImageChange}
          />

          <div className="d-flex flex-wrap justify-content-end align-items-start gap-3 mb-4">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm d-inline-flex align-items-center gap-2 text-white"
                style={{
                  backgroundColor: "#f97316",
                  padding: "8px 14px",
                  borderRadius: "8px",
                  fontWeight: 600,
                  border: "none",
                }}
                onClick={triggerFileDialog}
              >
                <i className="fas fa-paperclip" />
                แนบรูป
              </button>
              {imagePreview ? (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setIsImagePreviewOpen(true)}
                  >
                    ดูตัวอย่างรูป
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleRemoveImage}
                  >
                    ลบรูป
                  </button>
                </>
              ) : null}
              {mode === "U" && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm text-white"
                    style={{ backgroundColor: "#f97316", padding: "8px 14px", borderRadius: "8px", fontWeight: 600 }}
                    onClick={() => setIsHistoryOpen(true)}
                  >
                    ดูประวัติการซื้อสินค้า
                  </button>
                  <button type="button" className="btn btn-sm btn-link text-secondary" onClick={handleReset}>
                    เริ่มใหม่
                  </button>
                  <button type="button" className="btn btn-sm btn-link text-danger" onClick={handleDelete}>
                    ลบข้อมูล
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="rounded-3 shadow-sm bg-white p-4 mb-4 border-0">
            <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom">ข้อมูลสินค้า</h6>
            <div className="row g-3">
              <div className="col-lg-4 col-md-6">
                <label style={labelStyle}>
                  รหัสสินค้า {mode === "U" ? <span style={{ color: "#dc2626" }}>*</span> : null}
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="item_code"
                  value={formData.item_code || ""}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder={mode === "I" ? "ระบบจะสร้างรหัสให้อัตโนมัติหากไม่กรอก" : ""}
                />
              </div>
              <div className="col-lg-4 col-md-6">
                <label style={labelStyle}>
                  ชื่อสินค้า <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="item_name"
                  value={formData.item_name || ""}
                  onChange={handleChange}
                  style={inputStyle}
                  required
                />
              </div>
              <div className="col-lg-4 col-md-12">
                <label style={{ ...labelStyle, whiteSpace: "nowrap" }}>ชื่อสินค้า(อังกฤษ)</label>
                <input
                  type="text"
                  className="form-control"
                  name="item_name_en"
                  value={formData.item_name_en || ""}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>
              <div className="col-12">
                <label style={labelStyle}>หมายเหตุสินค้า</label>
                <textarea
                  className="form-control"
                  name="item_desc"
                  rows={4}
                  value={formData.item_desc || ""}
                  onChange={handleChange}
                  style={{ ...inputStyle, minHeight: "100px" }}
                />
              </div>
            </div>
          </div>

          <div className="rounded-3 shadow-sm bg-white p-4 mb-4 border-0">
            <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom">รายละเอียดสินค้า</h6>
            <div className="row g-3">
              <div className="col-lg-3 col-md-6">
                <label style={labelStyle}>ต้นทุน</label>
                <CurrencyInput
                  maxLength={10}
                  decimalsLimit={2}
                  decimalScale={2}
                  allowNegativeValue={false}
                  className="form-control text-end"
                  name="item_cost"
                  value={formData.item_cost || ""}
                  onValueChange={(value, name) => handleCurrencyChange(value, name)}
                  style={inputStyle}
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label style={labelStyle}>ราคาสินค้า</label>
                <CurrencyInput
                  maxLength={10}
                  decimalsLimit={2}
                  decimalScale={2}
                  allowNegativeValue={false}
                  className="form-control text-end"
                  name="item_price"
                  value={formData.item_price || ""}
                  onValueChange={(value, name) => handleCurrencyChange(value, name)}
                  style={inputStyle}
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label style={labelStyle}>หน่วยในคลัง <span style={{ color: "#dc2626" }}>*</span></label>
                <select
                  name="item_unit_st"
                  className="form-select"
                  value={formData.item_unit_st || ""}
                  onChange={handleChange}
                  style={selectStyle}
                >
                  <option value="">กรุณาเลือกหน่วยในคลัง</option>
                  {formData.item_unit_st && !unitDataList.some((u) => u.Unit_Name === formData.item_unit_st) && (
                    <option value={formData.item_unit_st}>{formData.item_unit_st} (เดิม)</option>
                  )}
                  {unitDataList.map((unit) => (
                    <option key={unit.Unit_Id} value={unit.Unit_Name}>
                      {unit.Unit_Name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-3 col-md-6">
                <label style={labelStyle}>
                  หมวดหมู่สินค้า <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <div className="input-group">
                  <select
                    name="item_group"
                    className="form-select"
                    value={formData.item_group || ""}
                    onChange={handleChange}
                    disabled={groupDataList.length <= 0}
                    style={selectStyle}
                  >
                    <option value="">กรุณาเลือกหมวดหมู่สินค้า</option>
                    {groupDataList.map((group) => (
                      <option key={group.Group_Id} value={group.Group_Id}>
                        {group.Group_Name}
                      </option>
                    ))}
                  </select>
                  {groupDataList.length <= 0 && (
                    <button
                      type="button"
                      onClick={() => navigate("/tb-set-group")}
                      className="btn text-white rounded-end"
                      style={{ backgroundColor: "#f97316" }}
                    >
                      +
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3 shadow-sm bg-white p-4 mb-2 border-0">
            <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom">รายละเอียดสต็อก</h6>
            <div className="row g-3">
              <div className="col-lg-4 col-md-6">
                <label style={labelStyle}>จุดสั่งซื้อ</label>
                <CurrencyInput
                  maxLength={10}
                  allowDecimals={false}
                  allowNegativeValue={false}
                  className="form-control text-end"
                  name="item_popoint"
                  value={formData.item_popoint || ""}
                  onValueChange={(value, name) => handleCurrencyChange(value, name)}
                  style={inputStyle}
                />
              </div>
              <div className="col-lg-4 col-md-6">
                <label style={{ ...labelStyle, whiteSpace: "nowrap" }}>จำนวนสูงสุดในสต็อก</label>
                <CurrencyInput
                  maxLength={10}
                  allowDecimals={false}
                  allowNegativeValue={false}
                  className="form-control text-end"
                  name="item_maxonhand"
                  value={formData.item_maxonhand || ""}
                  onValueChange={(value, name) => handleCurrencyChange(value, name)}
                  style={inputStyle}
                />
              </div>
              <div className="col-lg-4 col-md-6">
                <label style={labelStyle}>สถานะ</label>
                <select
                  className="form-select"
                  name="item_status"
                  value={formData.item_status || "Y"}
                  onChange={handleChange}
                  style={selectStyle}
                >
                  <option value="Y">ใช้งาน</option>
                  <option value="N">ไม่ใช้งาน</option>
                </select>
              </div>
            </div>
          </div>
        </InitialDataFormLayout>
      )}

      <ViewHistoryItem
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        itemId={parseCurrency(formData?.item_id || selectedData?.Item_Id, 10)}
      />
      {isImagePreviewOpen ? (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.55)" }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          onClick={() => setIsImagePreviewOpen(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            role="document"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">ตัวอย่างรูปสินค้า</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setIsImagePreviewOpen(false)}
                />
              </div>
              <div className="modal-body d-flex justify-content-center" style={{ minHeight: "300px" }}>
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="ตัวอย่างรูปสินค้า"
                    style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
                  />
                ) : (
                  <div className="text-muted">ยังไม่มีรูปสินค้า</div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Form;
